// src/pages/CreateMeetingPage/CreateMeetingPage.jsx
import { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight,
  Video,
  Users,
  Clock,
  Share2,
  Sparkles,
  User,
  Users2,
  UsersRound,
  Building2,
  ArrowLeft,
  CalendarClock,
  Calendar,
  AlarmClock,
} from "lucide-react";
import { createMeeting } from "@/Services/UserMeetServices";

const ROOM_OPTIONS = [
  {
    value: "4",
    label: "Huddle",
    subtitle: "Up to 4 participants",
    description: "Quick 1-on-1s & small syncs",
    icon: User,
  },
  {
    value: "6",
    label: "Team Room",
    subtitle: "Up to 6 participants",
    description: "Perfect for team standups",
    icon: Users2,
  },
  {
    value: "10",
    label: "Conference",
    subtitle: "Up to 10 participants",
    description: "Cross-team collaboration",
    icon: UsersRound,
  },
  {
    value: "50",
    label: "Town Hall",
    subtitle: "Up to 50 participants",
    description: "All-hands & presentations",
    icon: Building2,
  },
];

// ── Helpers ────────────────────────────────────────────────────────────────────

/** Returns a datetime-local string rounded up to the next full hour. */
function nextRoundHour(offsetMinutes = 60) {
  const d = new Date(Date.now() + offsetMinutes * 60 * 1000);
  d.setMinutes(0, 0, 0);
  // Shift to next hour
  d.setHours(d.getHours() + 1);
  // Format as "YYYY-MM-DDTHH:mm" (datetime-local input value)
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Format an ISO/datetime-local string for display. */
function formatScheduled(dtStr) {
  if (!dtStr) return null;
  try {
    return new Date(dtStr).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dtStr;
  }
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function CreateUserMeetingPage() {
  const navigate = useNavigate();

  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    roomType: "",
    // scheduling
    isScheduled: false,
    scheduledAt: nextRoundHour(60), // 1 h from now
    scheduledEndAt: nextRoundHour(120), // 2 h from now
  });

  // ── Derived state ────────────────────────────────────────────────────────────

  const selectedRoom = ROOM_OPTIONS.find((r) => r.value === form.roomType);

  /** Minimum value for the start datetime input — now. */
  const minDateTime = useMemo(() => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
  }, []);

  /** Minimum for end: must be after start. */
  const minEndDateTime = form.scheduledAt || minDateTime;

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const field = (key) => ({
    value: form[key],
    onChange: (e) => setForm((p) => ({ ...p, [key]: e.target.value })),
  });

  const handleStartChange = (e) => {
    const newStart = e.target.value;
    setForm((p) => {
      // Auto-bump end time to start + 1 h if end is now before new start
      let newEnd = p.scheduledEndAt;
      if (newStart && newEnd && newEnd <= newStart) {
        // Add 1 hour to new start
        const d = new Date(newStart);
        d.setHours(d.getHours() + 1);
        const pad = (n) => String(n).padStart(2, "0");
        newEnd = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      }
      return { ...p, scheduledAt: newStart, scheduledEndAt: newEnd };
    });
  };

  const toggleScheduled = () => {
    setForm((p) => ({ ...p, isScheduled: !p.isScheduled }));
  };

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      const title = form.title.trim();
      if (!title) return toast.error("Meeting title is required");
      if (!form.roomType) return toast.error("Please select a room size");

      // ── Scheduling validation ───────────────────────────────────────────────
      if (form.isScheduled) {
        if (!form.scheduledAt) {
          return toast.error("Please set a start date and time");
        }
        const startMs = new Date(form.scheduledAt).getTime();
        if (isNaN(startMs)) {
          return toast.error("Invalid start date/time");
        }
        if (startMs <= Date.now()) {
          return toast.error("Start time must be in the future");
        }
        if (form.scheduledEndAt) {
          const endMs = new Date(form.scheduledEndAt).getTime();
          if (!isNaN(endMs) && endMs <= startMs) {
            return toast.error("End time must be after start time");
          }
        }
      }

      try {
        setSubmitting(true);

        const payload = {
          title,
          roomType: form.roomType,
          ...(form.description.trim() && {
            description: form.description.trim(),
          }),
          ...(form.isScheduled && {
            scheduledAt: new Date(form.scheduledAt).toISOString(),
            ...(form.scheduledEndAt && {
              scheduledEndAt: new Date(form.scheduledEndAt).toISOString(),
            }),
          }),
        };

        const res = await createMeeting(payload);

        if (!res?.success) {
          throw new Error(res?.message || "Failed to create meeting");
        }

        const { joinCode } = res.data || {};

        toast.success(res.message || "Meeting created successfully!");

        if (form.isScheduled) {
          // Scheduled → go to dashboard where the "Upcoming" card will appear
          navigate("/user-meet", { replace: true });
        } else {
          // Instant → go straight to the join/lobby page
          navigate(`/user-meet/join/${joinCode}`, { replace: true });
        }
      } catch (err) {
        toast.error(err?.message || "Failed to create meeting");
      } finally {
        setSubmitting(false);
      }
    },
    [form, navigate],
  );

  // ── Render ───────────────────────────────────────────────────────────────────

  const previewItems = [
    {
      icon: Video,
      label: "Meeting Title",
      value: form.title || null,
      placeholder: "Give your meeting a name",
    },
    {
      icon: Sparkles,
      label: "Description",
      value: form.description || null,
      placeholder: "Optional context",
    },
    {
      icon: Users,
      label: "Room Size",
      value: selectedRoom
        ? `${selectedRoom.label} (${selectedRoom.subtitle})`
        : null,
      placeholder: "Select a room type",
    },
    form.isScheduled
      ? {
          icon: Calendar,
          label: "Scheduled",
          value: form.scheduledAt ? formatScheduled(form.scheduledAt) : null,
          placeholder: "Pick a start time",
        }
      : {
          icon: Clock,
          label: "Duration",
          value: "Unlimited",
          placeholder: "No time limit",
          isStatic: true,
        },
    {
      icon: Share2,
      label: "Access",
      value: "Shareable link",
      placeholder: "Anyone with the link",
      isStatic: true,
    },
  ];

  return (
    <div className="min-h-[80vh]">
      <div className="container-x grid md:grid-cols-12 gap-12 py-16 md:py-24">
        {/* ── Left panel ── */}
        <div className="md:col-span-5 hidden md:block">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="group mb-8 inline-flex items-center gap-2 text-white/55 transition-colors hover:text-[#C6A87C]"
          >
            <ArrowLeft
              size={15}
              className="transition-transform duration-200 group-hover:-translate-x-1"
            />
            <span className="overline font-display text-lg tracking-tight italic">
              Back
            </span>
          </button>

          <p className="overline mb-5">
            {form.isScheduled ? "Scheduled Meeting" : "Instant Meeting"}
          </p>
          <h1 className="font-display font-light text-5xl lg:text-6xl tracking-tighter leading-[0.95] mb-6">
            {form.isScheduled ? (
              <>
                Schedule your meeting,
                <br />
                <span className="italic text-[#C6A87C]">plan ahead.</span>
              </>
            ) : (
              <>
                Create your meeting,
                <br />
                <span className="italic text-[#C6A87C]">share instantly.</span>
              </>
            )}
          </h1>
          <p className="text-white/65 max-w-md leading-relaxed">
            {form.isScheduled
              ? "Schedule a meeting for a future date. Participants will receive a calendar invite and can join when it starts."
              : "Set up a new video meeting room in seconds. Choose your room size and share the link with your team."}
          </p>

          {/* Live preview */}
          <div className="mt-12 space-y-3">
            {previewItems.map((s, i) => {
              const Icon = s.icon;
              const hasValue = s.isStatic || (s.value && !s.isStatic);
              return (
                <div
                  key={i}
                  className={`flex items-center gap-3 text-sm transition-opacity ${
                    hasValue ? "opacity-100" : "opacity-40"
                  }`}
                >
                  <div
                    className={`w-2 h-2 rounded-full ${
                      hasValue ? "bg-[#C6A87C]" : "bg-white/20"
                    }`}
                  />
                  <Icon
                    size={13}
                    className={hasValue ? "text-[#C6A87C]" : "text-white/30"}
                  />
                  <span className="text-white/70">{s.label}</span>
                  {s.value ? (
                    <span className="text-[#C6A87C] text-xs truncate max-w-[200px]">
                      — {s.value}
                    </span>
                  ) : (
                    <span className="text-white/25 text-xs">
                      — {s.placeholder}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Right panel ── */}
        <div className="md:col-span-6 md:col-start-7">
          <div className="glass rounded-2xl p-8">
            {/* Header */}
            <div className="mb-8">
              <p className="overline mb-1">New Meeting</p>
              <h2 className="font-display text-3xl tracking-tight">
                Meeting Details
              </h2>
              <p className="text-white/50 text-sm mt-1">
                Configure your video meeting room
              </p>
            </div>

            <form onSubmit={onSubmit} className="space-y-5">
              {/* Meeting Title */}
              <label className="block">
                <span className="overline block mb-2">Meeting Title *</span>
                <input
                  required
                  {...field("title")}
                  placeholder="Team Standup"
                  maxLength={100}
                  className="w-full bg-white/[0.04] border border-white/[0.1] rounded-lg px-4 py-3
                             focus:border-[#C6A87C] focus:outline-none transition-colors
                             text-white placeholder:text-white/30 text-sm"
                />
              </label>

              {/* Description */}
              <label className="block">
                <span className="overline block mb-2">
                  Description (Optional)
                </span>
                <textarea
                  {...field("description")}
                  placeholder="Quick sync on Q3 planning and roadmap..."
                  rows={3}
                  maxLength={500}
                  className="w-full bg-white/[0.04] border border-white/[0.1] rounded-lg px-4 py-3
                             focus:border-[#C6A87C] focus:outline-none transition-colors
                             text-white placeholder:text-white/30 text-sm resize-none"
                />
                <span className="text-white/30 text-xs mt-1 block">
                  {form.description.length}/500 characters
                </span>
              </label>

              {/* Room Type */}
              <div>
                <span className="overline block mb-3">Room Size *</span>
                <div className="grid grid-cols-2 gap-3">
                  {ROOM_OPTIONS.map((room) => {
                    const Icon = room.icon;
                    const isSelected = form.roomType === room.value;
                    return (
                      <button
                        key={room.value}
                        type="button"
                        onClick={() =>
                          setForm((p) => ({ ...p, roomType: room.value }))
                        }
                        className={`group relative text-left rounded-xl p-4 transition-all duration-200 cursor-pointer
                          ${
                            isSelected
                              ? "bg-[#C6A87C]/[0.12] border-[#C6A87C]/60 border shadow-[0_0_20px_rgba(198,168,124,0.08)]"
                              : "bg-white/[0.03] border border-white/[0.08] hover:border-white/[0.15] hover:bg-white/[0.05]"
                          }`}
                      >
                        <div
                          className={`absolute top-3 right-3 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all
                          ${
                            isSelected
                              ? "border-[#C6A87C] bg-[#C6A87C]"
                              : "border-white/20"
                          }`}
                        >
                          {isSelected && (
                            <div className="w-1.5 h-1.5 rounded-full bg-black" />
                          )}
                        </div>
                        <Icon
                          size={20}
                          className={`mb-2.5 transition-colors ${
                            isSelected
                              ? "text-[#C6A87C]"
                              : "text-white/30 group-hover:text-white/50"
                          }`}
                        />
                        <h3
                          className={`text-sm font-medium tracking-tight transition-colors ${
                            isSelected ? "text-[#C6A87C]" : "text-white/80"
                          }`}
                        >
                          {room.label}
                        </h3>
                        <p
                          className={`text-xs mt-0.5 transition-colors ${
                            isSelected ? "text-[#C6A87C]/70" : "text-white/40"
                          }`}
                        >
                          {room.subtitle}
                        </p>
                        <p
                          className={`text-[11px] mt-1.5 leading-snug transition-colors ${
                            isSelected ? "text-white/50" : "text-white/25"
                          }`}
                        >
                          {room.description}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* ── Schedule Toggle ── */}
              <div
                className={`rounded-xl border transition-all duration-200 overflow-hidden ${
                  form.isScheduled
                    ? "border-[#C6A87C]/40 bg-[#C6A87C]/[0.05]"
                    : "border-white/[0.08] bg-white/[0.02]"
                }`}
              >
                {/* Toggle row */}
                <button
                  type="button"
                  onClick={toggleScheduled}
                  className="w-full flex items-center justify-between px-4 py-3.5 text-left"
                >
                  <div className="flex items-center gap-3">
                    <CalendarClock
                      size={16}
                      className={
                        form.isScheduled ? "text-[#C6A87C]" : "text-white/40"
                      }
                    />
                    <div>
                      <p
                        className={`text-sm font-medium ${
                          form.isScheduled ? "text-[#C6A87C]" : "text-white/70"
                        }`}
                      >
                        Schedule for later
                      </p>
                      <p className="text-xs text-white/30 mt-0.5">
                        {form.isScheduled
                          ? "Meeting will start at the chosen time"
                          : "Toggle to pick a future date & time"}
                      </p>
                    </div>
                  </div>

                  {/* Toggle pill */}
                  <div
                    className={`relative w-10 h-5 rounded-full transition-colors duration-200 flex-shrink-0 ${
                      form.isScheduled ? "bg-[#C6A87C]" : "bg-white/15"
                    }`}
                  >
                    <div
                      className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${
                        form.isScheduled ? "translate-x-5" : "translate-x-0.5"
                      }`}
                    />
                  </div>
                </button>

                {/* Datetime inputs — revealed when scheduled */}
                {form.isScheduled && (
                  <div className="px-4 pb-4 space-y-3 border-t border-[#C6A87C]/20 pt-3">
                    {/* Start time */}
                    <label className="block">
                      <div className="flex items-center gap-2 mb-1.5">
                        <AlarmClock size={13} className="text-[#C6A87C]" />
                        <span className="text-xs font-medium text-white/70 uppercase tracking-wider">
                          Start Time *
                        </span>
                      </div>
                      <input
                        type="datetime-local"
                        value={form.scheduledAt}
                        min={minDateTime}
                        onChange={handleStartChange}
                        required={form.isScheduled}
                        className="w-full bg-white/[0.04] border border-white/[0.1] rounded-lg px-4 py-2.5
                                   focus:border-[#C6A87C] focus:outline-none transition-colors
                                   text-white text-sm
                                   [color-scheme:dark]"
                      />
                    </label>

                    {/* End time */}
                    <label className="block">
                      <div className="flex items-center gap-2 mb-1.5">
                        <Clock size={13} className="text-white/40" />
                        <span className="text-xs font-medium text-white/70 uppercase tracking-wider">
                          End Time{" "}
                          <span className="text-white/30 normal-case">
                            (optional)
                          </span>
                        </span>
                      </div>
                      <input
                        type="datetime-local"
                        value={form.scheduledEndAt}
                        min={minEndDateTime}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            scheduledEndAt: e.target.value,
                          }))
                        }
                        className="w-full bg-white/[0.04] border border-white/[0.1] rounded-lg px-4 py-2.5
                                   focus:border-[#C6A87C] focus:outline-none transition-colors
                                   text-white text-sm
                                   [color-scheme:dark]"
                      />
                    </label>

                    <p className="text-[11px] text-white/30 leading-snug">
                      After creation you'll be taken to the dashboard. A
                      calendar invite can be sent to participants from the
                      meeting card.
                    </p>
                  </div>
                )}
              </div>

              {/* Info boxes */}
              <div className="space-y-3">
                <div className="flex items-start gap-2 bg-white/[0.03] border border-white/[0.06] rounded-xl p-4">
                  <Share2
                    size={16}
                    className="text-[#C6A87C] mt-0.5 flex-shrink-0"
                  />
                  <p className="text-white/40 text-xs leading-relaxed">
                    <strong className="text-white/60">Shareable link:</strong>{" "}
                    You'll receive a unique join code and link to share with
                    participants.
                  </p>
                </div>

                <div className="flex items-start gap-2 bg-white/[0.03] border border-white/[0.06] rounded-xl p-4">
                  <Video
                    size={16}
                    className="text-[#C6A87C] mt-0.5 flex-shrink-0"
                  />
                  <p className="text-white/40 text-xs leading-relaxed">
                    <strong className="text-white/60">Full permissions:</strong>{" "}
                    All participants can share audio, video, and screen.
                  </p>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={submitting}
                className="btn-gold w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {submitting
                  ? form.isScheduled
                    ? "Scheduling…"
                    : "Creating Meeting…"
                  : form.isScheduled
                    ? "Schedule Meeting"
                    : "Create Meeting"}
                {!submitting && <ArrowRight size={16} />}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
